// Core sound manager implementation

// Define types for our sound system
type SoundId = string;
type SoundBuffer = AudioBuffer;

interface SoundEffect {
  id: SoundId;
  url: string;
  buffer: SoundBuffer | null;
  volume: number;
  isPreloaded?: boolean; // Track if sound has been preloaded
}

// Singleton instance
let soundManagerInstance: SoundManager | null = null;

/**
 * SoundManager class to handle multiple sound effects with proper caching and fallback
 */
export class SoundManager {
  private audioContext: AudioContext | null = null;
  private sounds: Map<SoundId, SoundEffect> = new Map();
  private isInitialized: boolean = false;
  private initPromise: Promise<boolean> | null = null;
  
  // For tracking load states and fallbacks
  private loadPromises: Map<string, Promise<boolean>> = new Map();
  private preloadedAudioElements: Map<string, HTMLAudioElement> = new Map();
  private hasUnlockedAudio: boolean = false;
  private pendingUnlockCallbacks: Array<() => void> = [];
  private isUnlocking: boolean = false;
  
  constructor() { 
    // Create the singleton instance
    if (!soundManagerInstance) {
      soundManagerInstance = this;
    }
  }
  
  // Register a new sound effect
  public registerSound(id: SoundId, url: string, volume: number = 1.0): void {
    console.log(`Registering sound: ${id}`);
    this.sounds.set(id, {
      id,
      url,
      buffer: null,
      volume,
      isPreloaded: false
    });
  }
  
  // Preload a specific sound for immediate playback
  public async preloadSound(id: SoundId, highPriority: boolean = false): Promise<boolean> {
    const sound = this.sounds.get(id);
    if (!sound) {
      console.error(`Cannot preload unknown sound: ${id}`);
      return false;
    }
    
    // Skip if already preloaded
    if (sound.isPreloaded) {
      console.log(`Sound ${id} already preloaded`);
      return true;
    }
    
    console.log(`Preloading sound: ${id}${highPriority ? ' (high priority)' : ''}`);
    
    try {
      // Try to initialize audio context if needed
      if (!this.audioContext) {
        this.tryCreateAudioContext();
      }
      
      // Preload using Web Audio API if possible
      if (this.audioContext) {
        const loaded = await this.loadSound(id);
        if (loaded) {
          sound.isPreloaded = true;
          this.sounds.set(id, sound);
          console.log(`Sound ${id} preloaded successfully (Web Audio)`);
          return true;
        }
      }
      
      // Fallback: Preload using HTML Audio element
      await this.preloadWithAudioElement(id);
      sound.isPreloaded = true;
      this.sounds.set(id, sound);
      console.log(`Sound ${id} preloaded successfully (HTML Audio)`);
      return true;
    } catch (error) {
      console.error(`Failed to preload sound ${id}:`, error);
      return false;
    }
  }
  
  // Preload using HTML Audio element (works better for mobile)
  private preloadWithAudioElement(id: SoundId): Promise<boolean> {
    return new Promise((resolve) => {
      const sound = this.sounds.get(id);
      if (!sound) {
        resolve(false);
        return;
      }
      
      // Check if we already have this audio element
      if (this.preloadedAudioElements.has(id)) {
        resolve(true);
        return;
      }
      
      // Create and configure audio element
      const audio = new Audio();
      audio.src = sound.url;
      audio.preload = 'auto';
      audio.setAttribute('playsinline', 'true'); // Important for iOS
      
      // Set up event handlers
      audio.oncanplaythrough = () => {
        console.log(`Audio element preloaded: ${id}`);
        this.preloadedAudioElements.set(id, audio);
        resolve(true);
      };
      
      audio.onerror = () => {
        console.error(`Failed to preload audio element: ${id}`);
        resolve(false);
      };
      
      // Start loading
      audio.load();
      
      // Set a timeout to resolve anyway after 2 seconds
      setTimeout(() => {
        if (!this.preloadedAudioElements.has(id)) {
          console.warn(`Preload timeout for ${id}, continuing anyway`);
          this.preloadedAudioElements.set(id, audio);
          resolve(true);
        }
      }, 2000);
    });
  }
  
  // Initialize the audio system and preload sounds
  public initialize(): Promise<boolean> {
    // Return existing promise if already initializing
    if (this.initPromise) {
      return this.initPromise;
    }
    
    this.initPromise = new Promise<boolean>(async (resolve) => {
      if (this.isInitialized) {
        resolve(true);
        return;
      }
      
      try {
        // Create audio context with compatibility for older browsers
        this.tryCreateAudioContext();
        
        if (this.audioContext) {
          // Resume the audio context if it's in a suspended state (important for iOS/Safari)
          if (this.audioContext.state === 'suspended') {
            try {
              await this.audioContext.resume();
              console.log("AudioContext resumed from suspended state");
            } catch (e) {
              console.warn("Failed to resume AudioContext:", e);
              // Continue anyway - will try again on user interaction
            }
          }
        } else {
          console.warn("Could not create AudioContext, will use HTML Audio fallback");
        }
        
        // Initialize by registering for user interactions
        this.setupInteractionHandlers();
        
        // Preload both button sounds for immediate use — highest priority.
        // Awaiting both means by the time the user can hit Submit, the
        // HTMLAudioElements are warmed and play() resolves synchronously.
        await Promise.all([
          this.preloadSound('buttonDown', true).catch(e => {
            console.warn("Failed to preload buttonDown:", e);
          }),
          this.preloadSound('buttonUp', true).catch(e => {
            console.warn("Failed to preload buttonUp:", e);
          }),
        ]);

        // Start preloading other button sounds for immediate use
        this.preloadButtonSounds();
        
        this.isInitialized = true;
        console.log("Audio system initialized with", this.sounds.size, "sounds");
        resolve(true);
      } catch (err) {
        console.error("Error initializing audio system:", err);
        resolve(false);
      } finally {
        // Clear the promise reference so we can try again if needed
        this.initPromise = null;
      }
    });
    
    return this.initPromise;
  }
  
  // Try to create AudioContext safely
  private tryCreateAudioContext(): void {
    if (this.audioContext) return;
    
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      console.log("AudioContext created:", this.audioContext.state);
    } catch (e) {
      console.warn("Failed to create AudioContext:", e);
    }
  }
  
  // Set up handlers for user interactions to enable audio
  private setupInteractionHandlers(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    
    const unlockAudio = (e: Event) => {
      console.log(`Audio unlock triggered by ${e.type}`);
      this.tryUnlockAudio();
    };
    
    // Add more specific listeners for mobile interactions
    const interactionEvents = [
      'touchstart', 'touchend', 'mousedown', 'pointerdown', 
      'keydown', 'click'
    ];
    
    interactionEvents.forEach(event => {
      document.addEventListener(event, unlockAudio, { 
        once: true, 
        capture: true, // Use capture to get events before they reach components
        passive: true  // Improve performance on mobile
      });
    });
  }
  
  // Execute all pending callbacks after unlock
  private executePendingCallbacks(): void {
    // Execute and clear all pending callbacks
    if (this.pendingUnlockCallbacks.length > 0) {
      console.log(`Executing ${this.pendingUnlockCallbacks.length} pending callbacks after audio unlock`);
      this.pendingUnlockCallbacks.forEach(callback => {
        try {
          callback();
        } catch (e) {
          console.error("Error in audio unlock callback:", e);
        }
      });
      this.pendingUnlockCallbacks = [];
    }
  }
  
  // Public method to execute a function after audio is unlocked
  public runAfterUnlock(callback: () => void): void {
    if (this.hasUnlockedAudio) {
      // If already unlocked, execute immediately
      callback();
    } else {
      // Otherwise add to pending callbacks
      this.pendingUnlockCallbacks.push(callback);
      
      // Try to unlock immediately
      this.tryUnlockAudio();
    }
  }
  
  // Public method to unlock audio (can be called explicitly)
  public tryUnlockAudio(): void {
    // Prevent concurrent unlocking
    if (this.isUnlocking) return;
    
    if (this.hasUnlockedAudio) {
      // If already unlocked, just execute pending callbacks
      this.executePendingCallbacks();
      return;
    }
    
    this.isUnlocking = true;
    console.log("Attempting to unlock audio...");
    
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume().then(() => {
        console.log("AudioContext resumed through explicit unlock");
      }).catch(e => {
        console.warn("Failed to resume AudioContext:", e);
      }).finally(() => {
        // Play silent sounds regardless of resuming success
        this.playSilentSound();
        this.wakeUpPreloadedSounds();
      });
    } else {
      // If no context or not suspended, just try the silent sounds
      this.playSilentSound();
      this.wakeUpPreloadedSounds();
    }
    
    // Mark as unlocked after a short delay - even if silent sound fails
    // This allows button sounds to try to play even if silent sound fails
    setTimeout(() => {
      this.hasUnlockedAudio = true;
      this.isUnlocking = false;
      
      // Execute any pending callbacks
      this.executePendingCallbacks();
      
      console.log("Audio unlock completed");
    }, 100);
  }
  
  // Wake up any preloaded audio elements
  private wakeUpPreloadedSounds(): void {
    // Prime both buttonDown and buttonUp so the very first real tap has a warm
    // audio element ready to play synchronously.
    const primeIds = ['buttonDown', 'buttonUp'];
    for (const id of primeIds) {
      const audio = this.preloadedAudioElements.get(id);
      if (!audio) continue;

      const originalVolume = audio.volume;
      audio.volume = 0.001;

      const playPromise = audio.play();
      if (playPromise) {
        playPromise.then(() => {
          setTimeout(() => {
            audio.pause();
            audio.currentTime = 0;
            audio.volume = originalVolume;
            console.log(`Successfully primed ${id} audio element`);
          }, 10);
        }).catch(e => {
          audio.volume = originalVolume;
          console.warn(`Failed to prime ${id} audio element:`, e);
        });
      }
    }
  }
  
  // Play a silent sound to unlock audio on mobile
  private playSilentSound(): void {
    try {
      // Create a silent audio buffer
      if (this.audioContext) {
        const buffer = this.audioContext.createBuffer(1, 1, 22050);
        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.audioContext.destination);
        source.start(0);
        console.log("Silent sound played via AudioContext");
      }
      
      // Also try with HTML Audio (for iOS)
      const silence = new Audio("data:audio/mp3;base64,SUQzBAAAAAABEUgABABkYXRhAQAAAD////////////////////////////////////////8=");
      silence.volume = 0.01;
      silence.setAttribute('playsinline', 'true'); // Important for iOS
      const playPromise = silence.play();
      if (playPromise) {
        playPromise.catch(e => {
          console.warn("Silent HTML Audio failed (expected on first try):", e);
        });
      }
    } catch (e) {
      console.warn("Error playing silent sound:", e);
    }
  }
  
  // Preload the button sounds specifically for immediate use
  private preloadButtonSounds(): void {
    // ButtonDown should already be preloaded in initialize() with highest priority
    
    // Preload other button sounds
    const buttonSounds = ['buttonClick', 'buttonUp'];
    buttonSounds.forEach(id => {
      if (this.sounds.has(id)) {
        this.preloadSound(id, true).catch(e => {
          console.warn(`Failed to preload button sound ${id}:`, e);
        });
      }
    });
    
    // Preload fireworks with lower priority
    if (this.sounds.has('fireworks')) {
      this.preloadSound('fireworks', false).catch(e => {
        console.warn("Failed to preload fireworks sound:", e);
      });
    }
  }
  
  // Load a specific sound into memory
  private async loadSound(id: SoundId): Promise<boolean> {
    // Return existing load promise if already loading
    if (this.loadPromises.has(id)) {
      return this.loadPromises.get(id)!;
    }
    
    const sound = this.sounds.get(id);
    if (!sound || !this.audioContext) {
      console.error(`Cannot load sound: ${id} - Sound not registered or audio context not initialized`);
      return false;
    }
    
    // Skip if already loaded
    if (sound.buffer) {
      return true;
    }
    
    // Create a new load promise
    const loadPromise = new Promise<boolean>(async (resolve) => {
      try {
        const response = await fetch(sound.url);
        if (!response.ok) {
          throw new Error(`Failed to fetch sound: ${response.status} ${response.statusText}`);
        }
        
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer);
        
        // Update the sound with its buffer
        sound.buffer = audioBuffer;
        sound.isPreloaded = true;
        this.sounds.set(id, sound);
        
        console.log(`Sound loaded: ${id}`);
        resolve(true);
      } catch (err) {
        console.error(`Error loading sound (${id}):`, err);
        resolve(false);
      } finally {
        // Remove from tracking map
        this.loadPromises.delete(id);
      }
    });
    
    // Store the promise for tracking
    this.loadPromises.set(id, loadPromise);
    
    return loadPromise;
  }
  
  // Play a sound by ID
  public async playSound(id: SoundId): Promise<void> {
    const sound = this.sounds.get(id);
    if (!sound) {
      console.error(`Cannot play sound: ${id} - Sound not registered`);
      return;
    }
    
    // If a preloaded HTML Audio element exists, play it immediately within
    // the user gesture — do NOT wait on the async unlock first. On iOS the
    // unlock + play race loses the first event, so we mark audio as unlocked
    // synchronously and let the unlock side-effects happen in the background.
    if (this.preloadedAudioElements.has(id)) {
      try {
        const audio = this.preloadedAudioElements.get(id)!;

        if (!this.hasUnlockedAudio) {
          this.hasUnlockedAudio = true;
          // Fire the unlock for side-effects (silent prime, ctx resume) but
          // don't await — the gesture is being spent on `audio.play()` below.
          this.tryUnlockAudio();
        }

        // Reset if it's already playing
        if (!audio.paused) {
          audio.currentTime = 0;
        } else {
          // Set volume and play
          audio.volume = sound.volume;
          const playPromise = audio.play();

          if (playPromise) {
            playPromise.then(() => {
              console.log(`Playing preloaded audio element: ${id}`);
            }).catch(e => {
              console.warn(`Preloaded audio element failed: ${id}`, e);
              // Fall back to other methods
              this.playWithWebAudio(id) || this.playFallbackSound(id);
            });
          }
        }
        return;
      } catch (e) {
        console.warn(`Error with preloaded audio element: ${id}`, e);
      }
    }

    // No preloaded element — fall back to the original unlock-then-play path.
    if (!this.hasUnlockedAudio) {
      this.tryUnlockAudio();
    }

    
    // If no preloaded element or it failed, try resuming audio context
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        console.log("AudioContext resumed before playing sound");
      } catch (err) {
        console.error("Failed to resume AudioContext:", err);
      }
    }
    
    // Try Web Audio API
    const playedWithWebAudio = await this.playWithWebAudio(id);
    
    // If Web Audio failed, use fallback
    if (!playedWithWebAudio) {
      this.playFallbackSound(id);
    }
  }
  
  // Play with Web Audio API
  private async playWithWebAudio(id: SoundId): Promise<boolean> {
    // Skip if no AudioContext
    if (!this.audioContext) {
      return false;
    }
    
    const sound = this.sounds.get(id);
    if (!sound) return false;
    
    // Load the sound if not already loaded
    if (!sound.buffer) {
      try {
        const loaded = await this.loadSound(id);
        if (!loaded) return false;
      } catch (e) {
        console.warn(`Failed to load sound ${id}:`, e);
        return false;
      }
    }
    
    try {
      // Verify we have a buffer
      if (!sound.buffer) return false;
      
      // Create a source node
      const source = this.audioContext.createBufferSource();
      source.buffer = sound.buffer;
      
      // Create a gain node to control volume
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = sound.volume;
      
      // Connect the source to gain node and then to destination
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // Start playing immediately
      source.start(0);
      console.log(`Playing sound: ${id} (Web Audio API)`);
      return true;
    } catch (err) {
      console.error(`Error playing sound (${id}) with Web Audio:`, err);
      return false;
    }
  }
  
  // Fallback to basic Audio API if WebAudio fails
  private playFallbackSound(id: SoundId): void {
    const sound = this.sounds.get(id);
    if (!sound) return;
    
    try {
      console.log(`Using fallback audio for ${id}`);
      const fallbackSound = new Audio(sound.url);
      fallbackSound.volume = sound.volume;
      fallbackSound.setAttribute('playsinline', 'true'); // Important for iOS
      
      // For iOS, need to play it within user gesture to work
      const playPromise = fallbackSound.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.error(`Fallback audio playback failed for ${id}:`, err);
          
          // Try one more approach for mobile
          this.playLastResortSound(id);
        });
      }
    } catch (err) {
      console.error(`Could not create fallback audio for ${id}:`, err);
      this.playLastResortSound(id);
    }
  }
  
  // Last resort approach for iOS
  private playLastResortSound(id: SoundId): void {
    const sound = this.sounds.get(id);
    if (!sound) return;
    
    try {
      // Create with document.createElement which sometimes works better on iOS
      const audioEl = document.createElement('audio');
      audioEl.src = sound.url;
      audioEl.volume = sound.volume;
      audioEl.setAttribute('playsinline', 'true');
      audioEl.muted = false;
      
      // Append to DOM briefly (helps on some iOS versions)
      document.body.appendChild(audioEl);
      
      // Play and then clean up
      audioEl.play().catch(e => {
        console.error("Last resort audio failed:", e);
      }).finally(() => {
        setTimeout(() => {
          document.body.removeChild(audioEl);
        }, 1000);
      });
    } catch (e) {
      console.error("Last resort audio creation failed:", e);
    }
  }
  
  // Get all registered sound IDs
  public getSoundIds(): SoundId[] {
    return Array.from(this.sounds.keys());
  }
}

// Export the singleton accessor function
export const getSoundManager = (): SoundManager => {
  if (!soundManagerInstance) {
    soundManagerInstance = new SoundManager();
  }
  return soundManagerInstance;
};
